/**
 * Christian calendar season detection.
 * Returns the current season and associated upgrade copy.
 */

function getEasterDate(year) {
  // Anonymous Gregorian algorithm
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31) - 1 // 0-indexed
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month, day)
}

export function getCurrentSeason(date = new Date()) {
  const year = date.getFullYear()
  const easter = getEasterDate(year)

  // Key dates (all as timestamps for easy comparison)
  const easterMs = easter.getTime()
  const nowMs = date.getTime()
  const dayMs = 86400000

  // Lent: 40 days before Easter (Ash Wednesday = Easter - 46 days)
  const ashWednesday = new Date(easterMs - 46 * dayMs)
  const palmSunday = new Date(easterMs - 7 * dayMs)
  const _easterMonday = new Date(easterMs + dayMs)

  // Advent: 4 Sundays before Christmas (approx Dec 1–24)
  const christmasDay = new Date(year, 11, 25) // Dec 25
  const _adventStart = new Date(year, 11, 1) // Dec 1

  // New Year window: Dec 27 – Jan 7
  const _newYearStart = new Date(year, 11, 27)
  const _newYearEnd = new Date(year, 0, 7)
  const _prevNewYearStart = new Date(year - 1, 11, 27)

  // Holy Week: Palm Sunday through Holy Saturday
  const _holySaturday = new Date(easterMs - dayMs)

  // --- Detection (most specific first) ---

  // Easter Sunday
  if (
    date.getFullYear() === easter.getFullYear() &&
    date.getMonth() === easter.getMonth() &&
    date.getDate() === easter.getDate()
  ) {
    return {
      season: 'easter',
      label: 'Easter Sunday',
      emoji: '✝️',
      pricingBadge: 'He is Risen — Special Offer',
      pricingCopy:
        'Celebrate the resurrection with unlimited access to Scripture, apologetics, and study tools.',
      upgradeCopy:
        'He is risen! Start your Premium journey this Easter — unlimited guidance for your faith walk.',
      chatBanner:
        'He is Risen! 🌅 Celebrate with full Premium access — unlimited questions for your Easter journey.',
    }
  }

  // Holy Week (Palm Sunday through Holy Saturday)
  if (nowMs >= palmSunday.getTime() && nowMs < easterMs) {
    return {
      season: 'holy_week',
      label: 'Holy Week',
      emoji: '✝️',
      pricingBadge: 'Holy Week Offer',
      pricingCopy:
        "Walk through the final days of Jesus' earthly ministry with unlimited access to Scripture and theology.",
      upgradeCopy:
        'This Holy Week, go deeper — Premium members have unlimited access to Scripture, commentary, and apologetics.',
      chatBanner: 'Holy Week 🌿 Dive deeper into the Passion narrative with unlimited access.',
    }
  }

  // Lent (Ash Wednesday through Palm Sunday)
  if (nowMs >= ashWednesday.getTime() && nowMs < palmSunday.getTime()) {
    const daysLeft = Math.ceil((palmSunday.getTime() - nowMs) / dayMs)
    return {
      season: 'lent',
      label: 'Lent',
      emoji: '🕊️',
      pricingBadge: 'Lenten Season Offer',
      pricingCopy:
        'Fast from distraction, not from growth. Start your Lenten journey with unlimited faith guidance.',
      upgradeCopy: `Start your Lenten journey with unlimited guidance — ${daysLeft} days left to go deeper this season.`,
      chatBanner: `Lent 🕊️ ${daysLeft} days remaining — fast from distraction, not from growth.`,
    }
  }

  // Advent (Dec 1–24)
  if (date.getMonth() === 11 && date.getDate() >= 1 && date.getDate() <= 24) {
    const daysToChristmas = Math.ceil((christmasDay.getTime() - nowMs) / dayMs)
    return {
      season: 'advent',
      label: 'Advent',
      emoji: '🕯️',
      pricingBadge: 'Advent Season Offer',
      pricingCopy:
        'Make this Advent season meaningful — unlimited daily reflections, Scripture commentary, and more.',
      upgradeCopy: `${daysToChristmas} days until Christmas — make this Advent meaningful with unlimited faith guidance.`,
      chatBanner: `Advent 🕯️ ${daysToChristmas} days until Christmas — unlimited daily reflections with Premium.`,
    }
  }

  // New Year (Dec 27 – Jan 7)
  const isNewYear =
    (date.getMonth() === 11 && date.getDate() >= 27) ||
    (date.getMonth() === 0 && date.getDate() <= 7)
  if (isNewYear) {
    return {
      season: 'new_year',
      label: 'New Year',
      emoji: '🌅',
      pricingBadge: 'New Year, New Journey',
      pricingCopy:
        'Start the year with a daily faith habit — join Premium and never run out of questions.',
      upgradeCopy:
        'Start the year anchored in faith — Premium gives you unlimited daily guidance for the journey ahead.',
      chatBanner:
        '🌅 New Year — build a daily faith habit with unlimited access to Scripture and theology.',
    }
  }

  return null // No active season
}
